/**
 * Prompt Service
 * 
 * Fetches all prompts from Keywords AI and provides name-based lookup with code fallback.
 * 
 * ## Usage
 * 
 * ```typescript
 * const service = getPromptService();
 * await service.initialize();  // Fetches all prompts from Keywords AI
 * 
 * // Get a prompt by name, with fallback to code
 * const content = service.getPrompt('ORCHESTRATOR_CONTEXT_NOTE', CODE_FALLBACK);
 * ```
 */

import type { KeywordsPromptResponse } from './types';
import { 
    KEYWORDS_API_BASE, 
    KEYWORDS_CACHE_TTL, 
    KEYWORDS_FETCH_TIMEOUT 
} from '../../services/keywords/constants';
import { getKeywordsApiKey } from '../../services/keywords/authUtils';
import { withRetry, isRetryableHttpError } from '../../services/keywords/retryUtils';

// ═══════════════════════════════════════════════════════════════════════════
// PROMPT SERVICE CLASS
// ═══════════════════════════════════════════════════════════════════════════

export class PromptService {
    private promptCache: Map<string, KeywordsPromptResponse> = new Map();
    private cacheTimestamp: number = 0;
    private initialized: boolean = false;
    private initPromise: Promise<void> | null = null;
    
    // Background sync
    private syncIntervalId: number | null = null;
    private syncListeners: Set<() => void> = new Set();
    
    constructor() {
        console.log('[PromptService] Created');
        // Note: Don't auto-initialize here - GlobalStateManager may not be ready yet.
        // Callers should await initialize() after GSM is initialized.
        // The getPrompt() method has a safety-net that kicks off init if needed.
    }
    
    // Note: API key management moved to shared utility: services/keywords/authUtils.ts
    // Use getKeywordsApiKey() from authUtils instead of a local method.
    
    // ═══════════════════════════════════════════════════════════════════════
    // INITIALIZATION - Fetch all prompts
    // ═══════════════════════════════════════════════════════════════════════
    
    /**
     * Initialize by fetching all prompts from Keywords AI.
     * Call this once at startup or when cache expires.
     * Safe to call multiple times - will reuse in-flight promise.
     */
    public async initialize(): Promise<void> {
        // Check if cache is still valid
        if (this.initialized && Date.now() - this.cacheTimestamp < KEYWORDS_CACHE_TTL) {
            console.log('[PromptService] Using cached prompts');
            return;
        }
        
        // If initialization is already in progress, wait for it
        if (this.initPromise) {
            console.log('[PromptService] Waiting for in-flight initialization...');
            return this.initPromise;
        }
        
        // Create and store the initialization promise
        this.initPromise = this.doInitialize();
        
        try {
            await this.initPromise;
        } finally {
            // Clear the promise so future calls can re-initialize if cache expires
            this.initPromise = null;
        }
    }
    
    /**
     * Internal initialization logic
     */
    private async doInitialize(): Promise<void> {
        const apiKey = await getKeywordsApiKey();
        if (!apiKey) {
            // Don't set initialized=true here - allows retry when GSM becomes ready
            console.warn('[PromptService] No API key yet, will use code fallbacks (will retry later)');
            return;
        }
        
        try {
            // Fetch all pages of prompts with retry logic
            const allPrompts = await withRetry(
                () => this.fetchAllPrompts(apiKey),
                {
                    maxAttempts: 3,
                    delayMs: 1000,
                    backoffMultiplier: 2,
                    isRetryable: isRetryableHttpError,
                    onRetry: (attempt, error) => {
                        console.log(`[PromptService] Retry ${attempt}/3 after error: ${error.message}`);
                    },
                }
            );
            
            // Cache by name (uppercase for case-insensitive matching)
            this.promptCache.clear();
            for (const prompt of allPrompts) {
                if (prompt.name) {
                    this.promptCache.set(prompt.name.toUpperCase(), prompt);
                }
            }
            
            this.cacheTimestamp = Date.now();
            this.initialized = true;
            
            console.log(`[PromptService] ✅ Cached ${this.promptCache.size} prompts from Keywords AI`);
            console.log(`[PromptService] Names: ${Array.from(this.promptCache.keys()).join(', ')}`);
            
            if (this.promptCache.size > 0) {
                console.log(`[PromptService] ✅ Initialization complete - ${this.promptCache.size} prompts loaded`);
            } else {
                console.log('[PromptService] ⚠️ Initialization complete - no prompts returned (will use code fallbacks)');
            }
            
            // Broadcast sync event to listeners
            this.broadcastSync();
            
        } catch (error: any) {
            const reason = error.name === 'AbortError' ? 'timeout' : error.message;
            console.warn(`[PromptService] Failed to fetch prompts (${reason}), will use fallbacks`);
            // Network/timeout errors are transient - leave initialized=false to allow retry
            console.log('[PromptService] ⚠️ Network error - will retry later, using code fallbacks for now');
        }
    }
    
    /**
     * Fetch all prompts from Keywords AI, handling pagination.
     * The API returns paginated results with 'next' URL for subsequent pages.
     */
    private async fetchAllPrompts(apiKey: string): Promise<KeywordsPromptResponse[]> {
        const allPrompts: KeywordsPromptResponse[] = [];
        let nextUrl: string | null = `${KEYWORDS_API_BASE}/prompts?page_size=100`;
        let pageCount = 0;
        const maxPages = 10; // Safety limit
        
        while (nextUrl && pageCount < maxPages) {
            pageCount++;
            
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), KEYWORDS_FETCH_TIMEOUT);
            
            const response = await fetch(nextUrl, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${apiKey}`,
                    'Content-Type': 'application/json',
                },
                signal: controller.signal,
            });
            
            clearTimeout(timeoutId);
            
            if (!response.ok) {
                console.warn(`[PromptService] Failed to fetch prompts page ${pageCount}: ${response.status}`);
                // 4xx = client/auth error (won't retry), 5xx = server error (allow retry)
                if (response.status >= 400 && response.status < 500) {
                    this.initialized = true;  // Permanent failure - don't retry
                    console.log('[PromptService] ⚠️ Initialization failed (4xx) - will use code fallbacks');
                } else {
                    // 5xx - leave initialized=false to allow retry
                    console.log('[PromptService] ⚠️ Server error - will retry later, using code fallbacks for now');
                }
                break;
            }
            
            const data = await response.json();
            const results: KeywordsPromptResponse[] = data.results || [];
            allPrompts.push(...results);
            
            // Get next page URL (Keywords AI uses 'next' field for pagination)
            nextUrl = data.next || null;
            
            if (nextUrl) {
                console.log(`[PromptService] Fetched page ${pageCount} (${results.length} prompts), fetching next page...`);
            }
        }
        
        if (pageCount >= maxPages && nextUrl) {
            console.warn(`[PromptService] ⚠️ Reached max page limit (${maxPages}), some prompts may be missing`);
        }
        
        console.log(`[PromptService] Fetched ${allPrompts.length} total prompts across ${pageCount} page(s)`);
        return allPrompts;
    }
    
    // ═══════════════════════════════════════════════════════════════════════
    // PROMPT RETRIEVAL
    // ═══════════════════════════════════════════════════════════════════════
    
    /**
     * Get a prompt by name, with code fallback.
     * 
     * @param name - Prompt name (e.g., 'ORCHESTRATOR_CONTEXT_NOTE')
     * @param fallback - Code-defined fallback content (string or lazy function)
     * @returns Prompt content (from Keywords AI or fallback)
     * 
     * @example
     * // Eager fallback (always evaluated)
     * service.getPrompt('MY_PROMPT', MY_CONSTANT);
     * 
     * // Lazy fallback (only evaluated on cache miss)
     * service.getPrompt('MY_PROMPT', () => buildMyPrompt());
     */
    public getPrompt(name: string, fallback: string | (() => string)): string {
        // Trigger refresh if not initialized OR cache has expired
        // This is non-blocking - uses cached/fallback values while refreshing in background
        const shouldRefresh = !this.initialized || this.isCacheExpired();
        if (shouldRefresh && !this.initPromise) {
            if (this.isCacheExpired()) {
                console.log('[PromptService] Cache expired, refreshing prompts in background...');
            }
            this.initialize().catch((error) => {
                console.error('[PromptService] ❌ Background refresh failed:', error);
            });
        }
        
        const cached = this.promptCache.get(name.toUpperCase());
        
        if (cached) {
            // Extract content from the current_version.messages
            const content = this.extractContent(cached);
            if (content) {
                console.log(`[PromptService] ✅ Using Keywords AI prompt: ${name}`);
                return content;
            }
        }
        
        // Log differently based on whether initialization is complete
        if (!this.initialized) {
            console.log(`[PromptService] Using code fallback for: ${name} (still initializing...)`);
        } else {
            console.log(`[PromptService] Using code fallback for: ${name}`);
        }
        
        // Resolve lazy fallback if provided as function
        return typeof fallback === 'function' ? fallback() : fallback;
    }
    
    /**
     * Check if the cache has expired (TTL exceeded)
     */
    private isCacheExpired(): boolean {
        return this.cacheTimestamp > 0 && Date.now() - this.cacheTimestamp >= KEYWORDS_CACHE_TTL;
    }
    
    /**
     * Force refresh prompts from Keywords AI.
     * Clears cache and re-fetches all prompts.
     * Used by admin panel "Pull Latest" button.
     */
    public async forceRefresh(): Promise<{ success: boolean; count: number; error?: string }> {
        console.log('[PromptService] Force refreshing prompts...');
        
        // Clear cache
        this.promptCache.clear();
        this.cacheTimestamp = 0;
        this.initialized = false;
        
        try {
            await this.initialize();
            
            // Broadcast sync event after successful refresh
            if (this.initialized) {
                this.broadcastSync();
            }
            
            return {
                success: this.initialized,
                count: this.promptCache.size,
                error: this.initialized ? undefined : 'Failed to fetch prompts (check console for details)',
            };
        } catch (error) {
            return {
                success: false,
                count: 0,
                error: error instanceof Error ? error.message : String(error),
            };
        }
    }
    
    /**
     * Refresh a single prompt from Keywords AI.
     * More efficient than forceRefresh() for updating one section.
     * Used by admin panel "Pull" button on individual sections.
     */
    public async refreshSinglePrompt(name: string): Promise<{ success: boolean; error?: string }> {
        console.log(`[PromptService] Refreshing single prompt: ${name}`);
        
        const apiKey = await getKeywordsApiKey();
        if (!apiKey) {
            return {
                success: false,
                error: 'No API key available',
            };
        }
        
        try {
            // Fetch specific prompt by name
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), KEYWORDS_FETCH_TIMEOUT);
            
            const response = await fetch(`${KEYWORDS_API_BASE}/prompts?name=${encodeURIComponent(name)}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${apiKey}`,
                    'Content-Type': 'application/json',
                },
                signal: controller.signal,
            });
            
            clearTimeout(timeoutId);
            
            if (!response.ok) {
                return {
                    success: false,
                    error: `HTTP ${response.status}: ${response.statusText}`,
                };
            }
            
            const data = await response.json();
            const results: KeywordsPromptResponse[] = data.results || [];
            
            if (results.length === 0) {
                return {
                    success: false,
                    error: `Prompt "${name}" not found in Keywords AI`,
                };
            }
            
            // Update cache with the fetched prompt
            const prompt = results[0];
            this.promptCache.set(name.toUpperCase(), prompt);
            this.cacheTimestamp = Date.now();
            
            // Broadcast sync event to update UI
            this.broadcastSync();
            
            console.log(`[PromptService] ✅ Refreshed prompt: ${name}`);
            
            return { success: true };
        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : String(error),
            };
        }
    }
    
    // ═══════════════════════════════════════════════════════════════════════
    // BACKGROUND SYNC
    // ═══════════════════════════════════════════════════════════════════════
    
    /**
     * Start background sync that automatically refreshes prompts at regular intervals.
     * Call this once at app startup (in GlobalStateManager.initialize()).
     * 
     * @param intervalMs - Sync interval in milliseconds (default: 15 minutes)
     */
    public startBackgroundSync(intervalMs: number = 15 * 60 * 1000): void {
        if (this.syncIntervalId !== null) {
            console.log('[PromptService] Background sync already running');
            return;
        }
        
        console.log(`[PromptService] Starting background sync (every ${intervalMs / 1000 / 60} minutes)`);
        
        this.syncIntervalId = window.setInterval(() => {
            console.log('[PromptService] Background sync triggered...');
            this.forceRefresh()
                .then((result) => {
                    if (result.success) {
                        console.log(`[PromptService] ✅ Background sync complete: ${result.count} prompts`);
                        // Broadcast sync event (which will also clear errors in UI)
                    } else {
                        console.warn(`[PromptService] ⚠️ Background sync failed: ${result.error}`);
                        // Still broadcast so UI can update error state
                        this.broadcastSync();
                    }
                })
                .catch((error) => {
                    console.error('[PromptService] ❌ Background sync error:', error);
                    // Broadcast so UI can show error
                    this.broadcastSync();
                });
        }, intervalMs);
    }
    
    /**
     * Stop background sync.
     * Call this when shutting down or during cleanup.
     */
    public stopBackgroundSync(): void {
        if (this.syncIntervalId !== null) {
            console.log('[PromptService] Stopping background sync');
            window.clearInterval(this.syncIntervalId);
            this.syncIntervalId = null;
        }
    }
    
    /**
     * Subscribe to sync events.
     * Callback is invoked whenever prompts are refreshed (manual or automatic).
     * 
     * @param callback - Function to call when sync completes
     * @returns Unsubscribe function (synchronously returned)
     * 
     * @example
     * const unsubscribe = promptService.onSync(() => {
     *     console.log('Prompts refreshed!');
     *     refreshUI();
     * });
     * 
     * // Later, to unsubscribe:
     * unsubscribe();
     * 
     * @example Usage in React useEffect
     * React.useEffect(() => {
     *     const unsubscribe = promptService.onSync(() => {
     *         setRefreshTrigger(prev => prev + 1);
     *     });
     *     return unsubscribe; // Cleanup on unmount
     * }, []);
     */
    public onSync(callback: () => void): () => void {
        this.syncListeners.add(callback);
        
        // Return unsubscribe function
        return () => {
            this.syncListeners.delete(callback);
        };
    }
    
    /**
     * Broadcast sync event to all listeners.
     * Only broadcasts if there are active listeners.
     */
    private broadcastSync(): void {
        if (this.syncListeners.size === 0) {
            return; // No listeners, skip broadcasting
        }
        
        console.log(`[PromptService] Broadcasting sync event to ${this.syncListeners.size} listener(s)`);
        for (const listener of this.syncListeners) {
            try {
                listener();
            } catch (error) {
                console.error('[PromptService] Sync listener error:', error);
            }
        }
    }
    
    // REMOVED: getCachedPrompts - no external callers
    
    /**
     * Check if a prompt exists in Keywords AI cache
     */
    public hasPrompt(name: string): boolean {
        return this.promptCache.has(name.toUpperCase());
    }
    
    /**
     * Check if PromptService has completed initialization.
     * 
     * @returns True if prompts have been fetched (or attempted to fetch) from Keywords AI.
     * 
     * @example
     * const service = getPromptService();
     * if (service.isInitialized()) {
     *     console.log('Prompts are ready!');
     * } else {
     *     console.log('Still initializing or failed...');
     * }
     */
    public isInitialized(): boolean {
        return this.initialized;
    }
    
    /**
     * Check if a specific prompt is loaded from Keywords AI (not using code fallback).
     * Alias for hasPrompt() with clearer naming for UI purposes.
     * 
     * @param name - Prompt name to check (case-insensitive)
     * @returns True if the prompt exists in the cache (fetched from Keywords AI)
     * 
     * @example
     * const service = getPromptService();
     * if (service.isFromKeywordsAI('ORCHESTRATOR_INTRO')) {
     *     // Prompt is from Keywords AI
     * } else {
     *     // Using code fallback
     * }
     */
    public isFromKeywordsAI(name: string): boolean {
        return this.hasPrompt(name);
    }
    
    /**
     * Get the timestamp when prompts were last synced from Keywords AI.
     * 
     * @returns Unix timestamp (milliseconds) of last successful sync, or 0 if never synced.
     * 
     * @example
     * const service = getPromptService();
     * const lastSync = service.getLastSyncTime();
     * if (lastSync > 0) {
     *     const date = new Date(lastSync);
     *     console.log(`Last synced at: ${date.toLocaleTimeString()}`);
     * }
     */
    public getLastSyncTime(): number {
        return this.cacheTimestamp;
    }
    
    /**
     * Get the timestamp for a specific prompt.
     * 
     * @param name - Prompt name to check (case-insensitive)
     * @returns Unix timestamp (milliseconds) when this prompt was last fetched, or 0 if not in cache.
     * 
     * @example
     * const service = getPromptService();
     * const timestamp = service.getPromptTimestamp('ORCHESTRATOR_INTRO');
     * if (timestamp > 0) {
     *     console.log(`Fetched at: ${new Date(timestamp).toLocaleTimeString()}`);
     * }
     */
    public getPromptTimestamp(name: string): number {
        return this.hasPrompt(name) ? this.cacheTimestamp : 0;
    }
    
    // ═══════════════════════════════════════════════════════════════════════
    // VARIABLE DETECTION AND REPLACEMENT
    // ═══════════════════════════════════════════════════════════════════════
    
    /**
     * Detect all {{variables}} in a prompt template.
     * 
     * @param content - The prompt template content
     * @returns Array of unique variable names (without the {{ }} brackets)
     * 
     * @example
     * detectVariables('Hello {{name}}, you have {{count}} items')
     * // Returns: ['name', 'count']
     */
    public detectVariables(content: string): string[] {
        const matches = content.match(/\{\{(\w+)\}\}/g) || [];
        return [...new Set(matches.map(m => m.slice(2, -2)))];
    }
    
    /**
     * Replace {{variables}} with actual values.
     * Variables not found in the map are left as-is (kept with brackets).
     * 
     * @param content - The prompt template content with {{variables}}
     * @param variables - Map of variable names to their values
     * @returns Content with variables replaced
     * 
     * @example
     * replaceVariables('Hello {{name}}!', { name: 'World' })
     * // Returns: 'Hello World!'
     */
    public replaceVariables(
        content: string,
        variables: Record<string, string>
    ): string {
        return content.replace(/\{\{(\w+)\}\}/g, (match, varName) => {
            return variables[varName] ?? match; // Keep original if no value provided
        });
    }
    
    /**
     * Get prompt from Keywords AI and replace {{variables}}.
     * This is the main method agents should use for variable-enabled prompts.
     * 
     * @param name - Prompt name in Keywords AI
     * @param fallback - Code-defined fallback content
     * @param variables - Optional map of variable names to their values
     * @returns Prompt content with variables replaced
     * 
     * @example
     * // In Keywords AI: "Building: {{planTitle}} with {{slideCount}} slides"
     * getPromptWithVariables('MY_PROMPT', FALLBACK, {
     *     planTitle: 'Q4 Review',
     *     slideCount: '12'
     * })
     * // Returns: "Building: Q4 Review with 12 slides"
     */
    public getPromptWithVariables(
        name: string,
        fallback: string | (() => string),
        variables?: Record<string, string>
    ): string {
        let content = this.getPrompt(name, fallback);
        if (variables && Object.keys(variables).length > 0) {
            content = this.replaceVariables(content, variables);
        }
        return content;
    }
    
    // ═══════════════════════════════════════════════════════════════════════
    // COMPOSED SECTIONS
    // ═══════════════════════════════════════════════════════════════════════
    
    /**
     * Build a section from multiple atomic prompts.
     * 
     * Use this when a section is composed of smaller pieces that should
     * be independently versioned in Keywords AI.
     * 
     * @param options.prompts - Array of prompt names to compose (in order)
     * @param options.separator - String to join prompts with (default: '\n\n---\n\n')
     * @param options.fallbacks - Map of prompt names to their code fallbacks
     * @returns Composed prompt content
     * REMOVED: composeSection - no external callers
     */
    
    // ═══════════════════════════════════════════════════════════════════════
    // HELPERS
    // ═══════════════════════════════════════════════════════════════════════
    
    /**
     * Extract only the system message from a Keywords AI prompt response.
     * Content is in current_version.messages[0].content (system message only)
     * 
     * Note: Keywords AI can return content as string OR array of content parts
     * (for multimodal messages), so we handle both cases at runtime.
     */
    private extractContent(prompt: KeywordsPromptResponse): string | null {
        const messages = prompt.live_version?.messages;
        if (!messages || messages.length === 0) {
            return null;
        }
        
        // Find system message first, fallback to first message
        const systemMsg = messages.find((m) => m.role === 'system');
        // Use 'unknown' to handle runtime type variance from API
        const rawContent: unknown = systemMsg?.content || messages[0]?.content;
        
        // Handle both string and array content (multimodal messages)
        let content: string | null = null;
        if (typeof rawContent === 'string') {
            content = rawContent;
        } else if (Array.isArray(rawContent)) {
            // Extract text from content parts array
            const textParts = (rawContent as Array<{ type?: string; text?: string }>)
                .filter((part) => part.type === 'text' && typeof part.text === 'string')
                .map((part) => part.text as string);
            content = textParts.join('\n');
        }
        
        // Return content if it's not empty
        if (content && content.trim().length > 0) {
            return content;
        }
        
        return null;
    }
}

// ═══════════════════════════════════════════════════════════════════════════
// SINGLETON INSTANCE
// ═══════════════════════════════════════════════════════════════════════════

let promptServiceInstance: PromptService | null = null;

/**
 * Get the singleton PromptService instance
 */
export function getPromptService(): PromptService {
    if (!promptServiceInstance) {
        promptServiceInstance = new PromptService();
    }
    return promptServiceInstance;
}

/**
 * Reset the singleton (for testing)
 */
export function resetPromptService(): void {
    promptServiceInstance = null;
}
